import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Autocomplete,
  Badge,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  AttachMoney as MoneyIcon,
  Comment as CommentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalShipping as LocalShippingIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Verified as VerifiedIcon,
  QrCode as QrCodeIcon,
  Security as SecurityIcon,
  DateRange as DateRangeIcon,
  Store as StoreIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

import Sidebar from '../../../../components/sidebars/SideBarStoreKeep';
import TopNav from '../../../../components/TopNav_hotelStaff';

import QRCode from 'qrcode.react';

// ==================== CONSTANTS ====================
const REQUEST_STATUSES = {
  pending: { label: 'Pending', color: '#ff9800', icon: PendingIcon },
  account_approved: { label: 'Account Approved', color: '#2196f3', icon: CheckCircleIcon },
  gm_approved: { label: 'GM Approved', color: '#4caf50', icon: VerifiedIcon },
  partially_received: { label: 'Partially Received', color: '#ff9800', icon: LocalShippingIcon },
  completed: { label: 'Completed', color: '#4caf50', icon: CheckCircleIcon },
  rejected: { label: 'Rejected', color: '#f44336', icon: CancelIcon },
};

// Hardcoded department value
const HARDCODED_DEPARTMENT = 'Store Keeping';

// Generate a unique security token for PO
const generateSecurityToken = (poNumber, date) => {
  const str = `${poNumber}-${date}-${process.env.REACT_APP_PO_SECRET || 'ELEGANTE2026'}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).toUpperCase();
};

// ==================== HELPER FUNCTIONS ====================

const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.trim()
    .replace(/[<>]/g, '')
    .replace(/&[a-z]+;/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .replace(/[^\w\s\-.,()]/g, '');
};

const sanitizeNumber = (num) => {
  if (num === null || num === undefined || num === '') return 0;
  const parsed = parseFloat(num);
  return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₦0.00';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatCurrencyWords = (amount) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return 'Zero Naira';
  
  const naira = Math.floor(num);
  const kobo = Math.round((num - naira) * 100);
  
  const words = numberToWords(naira);
  let result = `${words} Naira`;
  if (kobo > 0) {
    result += ` and ${numberToWords(kobo)} Kobo`;
  }
  return result;
};

const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
    if (n < 1000000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    return convert(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + convert(n % 1000000) : '');
  };
  
  return convert(num);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return 'N/A';
  }
};

const logSecurityEvent = (eventType, details) => {
  console.debug(`[SECURITY-STOREKEEPER] ${eventType}:`, {
    timestamp: new Date().toISOString(),
    eventType,
    details,
    userAgent: navigator.userAgent,
  });
};

// ==================== MAIN COMPONENT ====================
const PurchaseRequests = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Data states
  const [requests, setRequests] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryItemsMap, setInventoryItemsMap] = useState(new Map());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form states
  const [createForm, setCreateForm] = useState({
    requested_by_phone: '',
    requested_by_name: '',
    department: HARDCODED_DEPARTMENT,
    delivery_date: '',
    notes: '',
    items: [{ item_name: '', quantity: '', unit_price: '', unit_of_measurement: '', specification: '', item_id: null, total_price: 0 }],
  });

  const [editForm, setEditForm] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [remarkText, setRemarkText] = useState('');
  const [approvalLevel, setApprovalLevel] = useState('account');

  // Item receipt states
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [receiptForm, setReceiptForm] = useState({
    is_received: true,
    received_quantity: 0,
    remark: '',
  });

  // User info - department is hardcoded
  const [userProfile, setUserProfile] = useState({
    phone: '',
    name: '',
    department: HARDCODED_DEPARTMENT,
    user_type: '',
  });

  // Autocomplete states for create dialog
  const [createItemSearchInput, setCreateItemSearchInput] = useState('');
  const [createItemSearchResults, setCreateItemSearchResults] = useState([]);
  const createItemSearchDebounce = useRef(null);

  // Autocomplete states for edit dialog
  const [editItemSearchInput, setEditItemSearchInput] = useState('');
  const [editItemSearchResults, setEditItemSearchResults] = useState([]);
  const [currentEditItemIndex, setCurrentEditItemIndex] = useState(null);
  const editItemSearchDebounce = useRef(null);

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // ==================== API CALLS ====================
  const getApiBaseUrl = () => process.env.REACT_APP_API_BASE_URL || '';

  const fetchInventoryItems = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      console.debug('[PurchaseRequests] Fetching inventory items from API...');
      
      const response = await axios.get(`${baseUrl}/hotel/inventory/purchase_requests.php?type=inventory_items`);
      
      console.debug('[PurchaseRequests] Inventory items API response:', response.data);
      
      if (response.data.success) {
        const items = response.data.items || [];
        setInventoryItems(items);
        
        const itemsMap = new Map();
        items.forEach(item => {
          itemsMap.set(item.item_name.toLowerCase(), {
            id: item.id,
            item_name: item.item_name,
            category: item.category,
            unit_of_measurement: item.unit_of_measurement || '',
            description: item.description || '',
          });
        });
        setInventoryItemsMap(itemsMap);
        
        console.debug(`[PurchaseRequests] Loaded ${items.length} inventory items from API`);
      } else {
        console.warn('[PurchaseRequests] Inventory items API returned success=false:', response.data.message);
      }
    } catch (err) {
      console.error('[PurchaseRequests] Error fetching inventory items:', err);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    const startTime = performance.now();
    console.debug('[PurchaseRequests] Fetching purchase requests...');

    try {
      setLoading(true);
      const baseUrl = getApiBaseUrl();
      let url = `${baseUrl}/hotel/inventory/purchase_requests.php`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const response = await axios.get(url);

      if (response.data.success) {
        const processedData = (response.data.data || []).map(req => ({
          ...req,
          items: req.items || [],
          total_items: req.items?.length || 0
        }));
        setRequests(processedData);
        setStats(response.data.stats);
        console.debug(`[PurchaseRequests] Loaded ${processedData.length} requests in ${(performance.now() - startTime).toFixed(2)}ms`);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      console.error('[PurchaseRequests] Fetch error:', err);
      setError('Failed to load purchase requests');
      setSnackbar({ open: true, message: 'Failed to load requests', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchUserProfile = useCallback(() => {
    try {
      // First try to get from userProfile object (where your login stores data)
      let userProfileData = null;
      const userProfileStr = localStorage.getItem('userProfile');
      
      if (userProfileStr) {
        try {
          userProfileData = JSON.parse(userProfileStr);
          console.debug('[PurchaseRequests] User profile from userProfile object:', userProfileData);
        } catch (e) {
          console.error('[PurchaseRequests] Error parsing userProfile:', e);
        }
      }
      
      // Get values from either userProfile object or direct localStorage keys
      const phoneNumber = userProfileData?.phone || 
                          localStorage.getItem('phoneNumber') || 
                          userProfileData?.phone_number || 
                          '';
      
      const userName = userProfileData?.name || 
                       localStorage.getItem('userName') || 
                       userProfileData?.full_name || 
                       userProfileData?.username ||
                       userProfileData?.email?.split('@')[0] ||
                       '';
      
      // IMPORTANT: Get user_type from userProfile object - this is where your login stores the role
      // Based on your login component, the role is stored as: 'General_Manager', 'Account', 'Storekeeping', etc.
      let userType = userProfileData?.user_type || 
                     userProfileData?.role || 
                     localStorage.getItem('role') || 
                     '';
      
      console.debug('[PurchaseRequests] Loading user profile:', {
        phone: phoneNumber,
        name: userName,
        user_type: userType,
        source: userProfileData ? 'userProfile object' : 'direct localStorage'
      });
      
      setUserProfile({
        phone: phoneNumber || '',
        name: userName || '',
        department: HARDCODED_DEPARTMENT,
        user_type: userType,
      });
      
      // Also update create form with requester info
      setCreateForm(prev => ({
        ...prev,
        requested_by_phone: phoneNumber || '',
        requested_by_name: userName || '',
        department: HARDCODED_DEPARTMENT,
      }));
      
    } catch (err) {
      console.error('[PurchaseRequests] Error loading user profile:', err);
    }
  }, []);

  // ==================== ADD TO INVENTORY SUPPLY FUNCTION ====================
  const addItemToInventorySupply = async (item, request, receivedQuantity) => {
    console.debug('[PurchaseRequests] Adding item to inventory supply:', {
      item_name: item.item_name,
      received_quantity: receivedQuantity,
      po_number: request.po_number,
      unit_price: item.unit_price
    });

    try {
      const baseUrl = getApiBaseUrl();
      
      // Find matching inventory item to get category and unit_of_measurement
      const inventoryItem = inventoryItemsMap.get(item.item_name.toLowerCase());
      
      // Calculate total invoice value based on quantity received and unit price
      const totalInvoiceValue = sanitizeNumber(receivedQuantity) * sanitizeNumber(item.unit_price);
      const quantityReceived = sanitizeNumber(receivedQuantity);
      
      const supplyData = {
        item_name: sanitizeString(item.item_name),
        category: inventoryItem?.category || 'General',
        unit_of_measurement: sanitizeString(item.unit_of_measurement) || 'pcs',
        quantity_received: quantityReceived,
        total_invoice_value: totalInvoiceValue,
        added_by_staffid: userProfile.name || userProfile.phone || 'Storekeeper',
        checked_by: userProfile.name || 'Storekeeper',
        expiry_date: null,
        date_received: new Date().toISOString().split('T')[0],
        backdate_reason: null,
        notes: `PO: ${request.po_number} | Item received on ${new Date().toISOString().split('T')[0]}`
      };
      
      console.debug('[PurchaseRequests] Sending supply data to API:', supplyData);
      
      const response = await axios.post(`${baseUrl}/hotel/inventory/add_supply2.php`, supplyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data && response.data.success) {
        console.debug('[PurchaseRequests] ✅ Supply record created successfully');
        console.debug('[PurchaseRequests] Supply ID:', response.data.id);
        
        logSecurityEvent('INVENTORY_SUPPLY_ADDED', {
          supply_id: response.data.id,
          item_name: item.item_name,
          quantity_received: quantityReceived,
          po_number: request.po_number,
        });
        
        return { 
          success: true, 
          supply_id: response.data.id, 
          message: response.data.message, 
          quantity_added: quantityReceived,
        };
      } else {
        console.error('[PurchaseRequests] Failed to add to inventory supply:', response.data?.message);
        return { success: false, error: response.data?.message || 'Unknown error' };
      }
    } catch (err) {
      console.error('[PurchaseRequests] Error adding to inventory supply:', err);
      console.error('[PurchaseRequests] Error details:', err.response?.data);
      logSecurityEvent('INVENTORY_SUPPLY_ERROR', {
        item_name: item.item_name,
        error: err.message,
        response: err.response?.data
      });
      return { success: false, error: err.response?.data?.message || err.message };
    }
  };

  // ==================== ITEM RECEIPT HANDLER ====================
  const handleUpdateItemReceipt = async () => {
    if (!selectedRequest || !selectedItem) {
      console.warn('[PurchaseRequests] No request or item selected for receipt');
      return;
    }

    const receivedQty = sanitizeNumber(receiptForm.received_quantity);
    const orderedQty = sanitizeNumber(selectedItem.quantity);
    const alreadyReceived = sanitizeNumber(selectedItem.received_quantity || 0);
    const newTotalReceived = alreadyReceived + receivedQty;

    console.debug('[PurchaseRequests] Processing item receipt:', {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
      item_id: selectedItem.id,
      item_name: selectedItem.item_name,
      ordered_quantity: orderedQty,
      already_received: alreadyReceived,
      received_quantity: receivedQty,
      new_total: newTotalReceived
    });

    if (receiptForm.is_received && receivedQty <= 0) {
      setSnackbar({ open: true, message: 'Please enter the received quantity', severity: 'error' });
      return;
    }

    if (newTotalReceived > orderedQty) {
      setSnackbar({ 
        open: true, 
        message: `Total received (${newTotalReceived}) cannot exceed ordered quantity (${orderedQty})`, 
        severity: 'error' 
      });
      return;
    }

    setLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      
      // Update the purchase request item receipt status
      const response = await axios.put(`${baseUrl}/hotel/inventory/purchase_requests.php`, {
        id: selectedRequest.id,
        action: 'update_item_received',
        item_id: selectedItem.id,
        is_received: newTotalReceived >= orderedQty ? 1 : 0,
        received_quantity: newTotalReceived,
        remark: sanitizeString(receiptForm.remark),
        staff_name: userProfile.name || 'Storekeeper',
      });

      console.debug('[PurchaseRequests] Purchase request update response:', response.data);

      if (response.data.success) {
        console.debug('[PurchaseRequests] Item receipt updated successfully');
        
        // Add to inventory supplies
        if (receivedQty > 0) {
          console.debug('[PurchaseRequests] Adding received item to inventory supplies...');
          
          const supplyResult = await addItemToInventorySupply(selectedItem, selectedRequest, receivedQty);
          
          if (supplyResult.success) {
            setSnackbar({ 
              open: true, 
              message: `${response.data.message} | ✅ Item added to inventory supplies (Supply ID: ${supplyResult.supply_id})`, 
              severity: 'success' 
            });
          } else {
            setSnackbar({ 
              open: true, 
              message: `${response.data.message} | ⚠️ WARNING: Failed to add to inventory: ${supplyResult.error}`, 
              severity: 'warning' 
            });
          }
        } else {
          setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        }
        
        setReceiptDialogOpen(false);
        setSelectedItem(null);
        setReceiptForm({ is_received: true, received_quantity: 0, remark: '' });
        fetchRequests();
        
        if (viewDialogOpen && selectedRequest) {
          const refreshResponse = await axios.get(`${baseUrl}/hotel/inventory/purchase_requests.php?id=${selectedRequest.id}`);
          if (refreshResponse.data.success) {
            setSelectedRequest(refreshResponse.data.data);
            console.debug('[PurchaseRequests] Refreshed selected request data');
          }
        }
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
        console.error('[PurchaseRequests] Purchase request update failed:', response.data.message);
      }
    } catch (err) {
      console.error('[PurchaseRequests] Error updating receipt:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to update receipt status', 
        severity: 'error' 
      });
      logSecurityEvent('ITEM_RECEIPT_ERROR', {
        request_id: selectedRequest.id,
        item_id: selectedItem.id,
        error: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== BULK ITEM RECEIPT HANDLER ====================
  const handleBulkItemReceipt = async () => {
    if (!selectedRequest) {
      console.warn('[PurchaseRequests] No request selected for bulk receipt');
      return;
    }
    
    // Get items that are not fully received
    const pendingItems = (selectedRequest.items || []).filter(item => {
      const isFullyReceived = item.is_received && (item.received_quantity >= item.quantity);
      return !isFullyReceived && item.quantity > 0;
    });
    
    console.debug('[PurchaseRequests] Bulk receipt initiated:', {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
      pending_items_count: pendingItems.length,
      pending_items: pendingItems.map(i => ({ 
        name: i.item_name, 
        ordered_qty: i.quantity,
        already_received: i.received_quantity || 0,
        pending_qty: i.quantity - (i.received_quantity || 0)
      }))
    });
    
    if (pendingItems.length === 0) {
      setSnackbar({ open: true, message: 'No pending items to receive', severity: 'warning' });
      return;
    }
    
    const totalPendingQty = pendingItems.reduce((sum, item) => sum + (item.quantity - (item.received_quantity || 0)), 0);
    
    if (!window.confirm(`⚠️ You are about to receive ${totalPendingQty} units across ${pendingItems.length} item(s).\n\nThis will mark all items as fully received and add them to inventory supplies.\n\nClick OK to confirm.`)) {
      console.debug('[PurchaseRequests] Bulk receipt cancelled by user');
      return;
    }
    
    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    const failedItems = [];
    const successfulItems = [];
    
    try {
      const baseUrl = getApiBaseUrl();
      
      for (const item of pendingItems) {
        const remainingQty = item.quantity - (item.received_quantity || 0);
        
        console.debug(`[PurchaseRequests] Processing bulk item: ${item.item_name}`, {
          item_id: item.id,
          ordered_qty: item.quantity,
          already_received: item.received_quantity || 0,
          receiving_now: remainingQty
        });
        
        // Update purchase request item
        const updateResponse = await axios.put(`${baseUrl}/hotel/inventory/purchase_requests.php`, {
          id: selectedRequest.id,
          action: 'update_item_received',
          item_id: item.id,
          is_received: 1,
          received_quantity: item.quantity,
          remark: `Bulk receipt - Received ${remainingQty} ${item.unit_of_measurement || 'units'} on ${new Date().toISOString().split('T')[0]}`,
          staff_name: userProfile.name || 'Storekeeper',
        });
        
        if (updateResponse.data.success) {
          console.debug(`[PurchaseRequests] Item ${item.item_name} receipt updated successfully`);
          
          // Add to inventory supplies
          const supplyResult = await addItemToInventorySupply(item, selectedRequest, remainingQty);
          
          if (supplyResult.success) {
            successCount++;
            successfulItems.push(`${item.item_name} (${remainingQty} ${item.unit_of_measurement || 'units'})`);
            console.debug(`[PurchaseRequests] Successfully processed item: ${item.item_name} (Supply ID: ${supplyResult.supply_id})`);
          } else {
            failCount++;
            failedItems.push(item.item_name);
            console.error(`[PurchaseRequests] Failed to add item to inventory: ${item.item_name}`, supplyResult.error);
          }
        } else {
          failCount++;
          failedItems.push(item.item_name);
          console.error(`[PurchaseRequests] Failed to update item: ${item.item_name}`, updateResponse.data.message);
        }
        
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      await fetchRequests();
      
      if (viewDialogOpen && selectedRequest) {
        const refreshResponse = await axios.get(`${baseUrl}/hotel/inventory/purchase_requests.php?id=${selectedRequest.id}`);
        if (refreshResponse.data.success) {
          setSelectedRequest(refreshResponse.data.data);
        }
      }
      
      logSecurityEvent('BULK_ITEM_RECEIPT_COMPLETED', {
        request_id: selectedRequest.id,
        po_number: selectedRequest.po_number,
        success_count: successCount,
        fail_count: failCount,
        successful_items: successfulItems,
        failed_items: failedItems
      });
      
      if (failCount === 0) {
        setSnackbar({ 
          open: true, 
          message: `✅ All ${successCount} item(s) (${totalPendingQty} units) received and added to inventory supplies successfully!`, 
          severity: 'success' 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: `⚠️ ${successCount} item(s) processed, ${failCount} failed. Failed: ${failedItems.join(', ')}`, 
          severity: 'warning' 
        });
      }
      
    } catch (err) {
      console.error('[PurchaseRequests] Error in bulk receipt:', err);
      setSnackbar({ 
        open: true, 
        message: 'Bulk receipt failed: ' + (err.response?.data?.message || err.message), 
        severity: 'error' 
      });
      logSecurityEvent('BULK_ITEM_RECEIPT_ERROR', {
        request_id: selectedRequest.id,
        error: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== CHECK ALL ITEMS RECEIVED ====================
  const areAllItemsReceived = (request) => {
    if (!request || !request.items) return false;
    const pendingItems = request.items.filter(item => {
      const isFullyReceived = item.is_received && (item.received_quantity >= item.quantity);
      return !isFullyReceived;
    });
    return pendingItems.length === 0;
  };

  // ==================== PURCHASE ORDER GENERATION ====================
  const generatePurchaseOrderHTML = (request) => {
    const securityToken = generateSecurityToken(request.po_number, request.created_at);
    const printDate = new Date();
    
    const isFullyApproved = request.status === 'gm_approved';
    
    let approvalStatusBadge = '';
    let approvalWarning = '';
    
    if (isFullyApproved) {
      approvalStatusBadge = '<div class="approved-stamp">✓ FULLY APPROVED FOR PROCUREMENT ✓</div>';
    } else if (request.status === 'account_approved' && !request.gm_approved_at) {
      approvalStatusBadge = '<div class="warning-stamp">⚠ ACCOUNT APPROVED - AWAITING GM APPROVAL ⚠</div>';
      approvalWarning = '<div class="warning-box">⚠ WARNING: This Purchase Order has only been approved by Account. GM approval is still pending. Use with caution.</div>';
    } else {
      approvalStatusBadge = '<div class="pending-stamp">⚠ PENDING APPROVAL - USE WITH CAUTION ⚠</div>';
    }
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>PURCHASE ORDER - ${request.po_number}</title>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; background: #e0e0e0; padding: 20px; }
          .po-container { max-width: 800px; margin: 0 auto; background: white; border-radius: 5px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
          .header { background: #C84646; color: white; padding: 15px 20px; text-align: center; }
          .header h1 { font-size: 28px; letter-spacing: 2px; margin-bottom: 5px; }
          .header p { font-size: 11px; opacity: 0.9; }
          .security-tag { font-family: monospace; font-size: 10px; text-align: right; padding: 5px 15px; background: #f5f5f5; color: #666; border-bottom: 1px solid #ddd; }
          .body { padding: 20px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 15px; padding: 10px 12px; background: #f8f9fa; border-radius: 4px; font-size: 13px; }
          .info-row span:first-child { font-weight: bold; color: #C84646; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th { background: #C84646; color: white; padding: 8px; font-size: 12px; text-align: left; }
          td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
          .total-row { background: #f5f5f5; font-weight: bold; }
          .notes { margin: 15px 0; padding: 10px; background: #fff3e0; border-left: 3px solid #ff9800; font-size: 12px; }
          .warning-box { margin: 15px 0; padding: 12px; background: #ffebee; border-left: 4px solid #f44336; border-radius: 4px; color: #c62828; font-size: 12px; font-weight: bold; text-align: center; }
          .approvers { display: flex; justify-content: space-between; margin: 20px 0 15px; padding: 15px; background: #f8f9fa; border-radius: 4px; flex-wrap: wrap; gap: 10px; }
          .approver { text-align: center; width: 23%; min-width: 120px; }
          .approver .role { font-weight: bold; color: #C84646; font-size: 11px; margin-bottom: 8px; }
          .approver .name { font-size: 12px; font-weight: bold; margin-bottom: 4px; }
          .approver .date { font-size: 9px; color: #666; }
          .approver .not-approved { color: #ff9800; font-size: 11px; font-style: italic; }
          .approver .missing { color: #f44336; font-size: 11px; font-style: italic; font-weight: bold; }
          .footer { margin-top: 15px; padding: 10px; background: #f8f9fa; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #ddd; }
          .approved-stamp { text-align: center; margin: 10px 20px; padding: 6px; background: #4caf50; color: white; font-weight: bold; font-size: 14px; border-radius: 3px; }
          .warning-stamp { text-align: center; margin: 10px 20px; padding: 6px; background: #ff9800; color: white; font-weight: bold; font-size: 13px; border-radius: 3px; }
          .pending-stamp { text-align: center; margin: 10px 20px; padding: 6px; background: #f44336; color: white; font-weight: bold; font-size: 14px; border-radius: 3px; }
          @media print { body { background: white; padding: 0; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="po-container">
          <div class="no-print" style="text-align: right; padding: 10px; background: #f5f5f5;">
            <button onclick="window.print();" style="padding: 5px 15px; background: #C84646; color: white; border: none; border-radius: 3px; cursor: pointer; margin-right: 5px;">🖨️ Print</button>
            <button onclick="window.close();" style="padding: 5px 15px; background: #666; color: white; border: none; border-radius: 3px; cursor: pointer;">✖ Close</button>
          </div>
          
          <div class="header">
            <h1>PURCHASE ORDER</h1>
            <p>Hotel Items</p>
          </div>
          
          <div class="security-tag">
            🔒 SECURE DOCUMENT | PO: ${request.po_number} | Token: ${securityToken.substring(0, 8)}
          </div>
          
          ${approvalStatusBadge}
          ${approvalWarning}
          
          <div class="body">
            <div class="info-row">
              <span>📄 PO Number:</span> <span>${sanitizeString(request.po_number)}</span>
              <span>📅 Date:</span> <span>${printDate.toLocaleDateString()}</span>
              <span>👤 Requester:</span> <span>${sanitizeString(request.requested_by_name || 'N/A')}</span>
            </div>
            
            <div class="info-row">
              <span>🏢 Department:</span> <span>${sanitizeString(request.department || 'Store Keeping')}</span>
              <span>📦 Delivery:</span> <span>${request.delivery_date ? formatDate(request.delivery_date) : 'Not Specified'}</span>
              <span>💰 Total:</span> <span style="color:#C84646; font-weight:bold;">₦${sanitizeNumber(request.total_amount).toLocaleString()}</span>
            </div>
            
            <table>
              <thead>
                <tr><th>#</th><th>Item Description</th><th>Qty</th><th>Unit</th><th align="right">Unit Price</th><th align="right">Total</th></tr>
              </thead>
              <tbody>
                ${(request.items || []).map((item, idx) => `
                  <tr>
                    <td>${idx + 1}</td>
                    <td>${sanitizeString(item.item_name)}${item.specification ? `<br><small style="color:#666;">${sanitizeString(item.specification)}</small>` : ''}</td>
                    <td>${sanitizeNumber(item.quantity).toLocaleString()}</td>
                    <td>${sanitizeString(item.unit_of_measurement || 'pcs')}</td>
                    <td align="right">₦${sanitizeNumber(item.unit_price).toLocaleString()}</td>
                    <td align="right">₦${sanitizeNumber(item.total_price).toLocaleString()}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td colspan="5" align="right"><strong>GRAND TOTAL</strong></td>
                  <td align="right"><strong>₦${sanitizeNumber(request.total_amount).toLocaleString()}</strong></td>
                </tr>
              </tbody>
            </table>
            
            ${request.notes ? `<div class="notes"><strong>📝 Notes:</strong> ${sanitizeString(request.notes)}</div>` : ''}
            
            <div class="approvers">
              <div class="approver">
                <div class="role">📝 REQUESTED BY</div>
                <div class="name">${sanitizeString(request.requested_by_name || '_________________')}</div>
                <div class="date">${formatDate(printDate)}</div>
              </div>
              <div class="approver">
                <div class="role">✓ ACCOUNT OFFICER</div>
                ${request.account_approved_by ? 
                  `<div class="name" style="color:#4caf50;">${sanitizeString(request.account_approved_by)} ✓</div>
                   <div class="date">${formatDate(request.account_approved_at)}</div>` : 
                  `<div class="missing">❌ NOT APPROVED</div>`}
              </div>
              <div class="approver">
                <div class="role">⭐ GENERAL MANAGER</div>
                ${request.gm_approved_by ? 
                  `<div class="name" style="color:#4caf50;">${sanitizeString(request.gm_approved_by)} ✓</div>
                   <div class="date">${formatDate(request.gm_approved_at)}</div>` : 
                  `<div class="missing">❌ NOT APPROVED</div>`}
              </div>
              <div class="approver">
                <div class="role">📦 STORE KEEPER</div>
                <div class="name">${userProfile.name || '_________________'}</div>
                <div class="date">${formatDate(printDate)}</div>
              </div>
            </div>
            
            <div class="footer">
              <p>🔐 Security Token: ${securityToken} | Valid for 30 days</p>
              <p>Generated: ${printDate.toLocaleString()} | Computer-generated document</p>
              ${!isFullyApproved ? '<p style="color:#f44336; font-weight:bold;">⚠ This document has incomplete approvals. Verify before processing.</p>' : ''}
            </div>
          </div>
        </div>
        <script>console.log('PO Generated: ${request.po_number}');</script>
      </body>
      </html>
    `;
  };

  const handlePrintPO = useCallback(async () => {
    if (!selectedRequest) {
      setSnackbar({ open: true, message: 'No purchase order selected', severity: 'error' });
      return;
    }

    logSecurityEvent('PURCHASE_ORDER_PRINT_ATTEMPT', {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
      status: selectedRequest.status,
      generated_by: userProfile.name,
    });

    setPrintLoading(true);

    try {
      const printWindow = window.open('', '_blank', 'width=1100,height=800,scrollbars=yes,resizable=yes');
      if (!printWindow) {
        throw new Error('Popup blocked. Please allow pop-ups for this site.');
      }

      const html = generatePurchaseOrderHTML(selectedRequest);
      
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
      }, 1000);
      
      logSecurityEvent('PURCHASE_ORDER_PRINT_SUCCESS', {
        request_id: selectedRequest.id,
        po_number: selectedRequest.po_number,
      });
      
      setSnackbar({ 
        open: true, 
        message: `Purchase Order #${selectedRequest.po_number} generated successfully with security features!`, 
        severity: 'success' 
      });
    } catch (err) {
      console.error('[PurchaseRequests] Print error:', err);
      setSnackbar({ 
        open: true, 
        message: err.message || 'Failed to generate purchase order', 
        severity: 'error' 
      });
      logSecurityEvent('PURCHASE_ORDER_PRINT_ERROR', {
        request_id: selectedRequest.id,
        error: err.message,
      });
    } finally {
      setPrintLoading(false);
    }
  }, [selectedRequest, userProfile.name]);

  // ==================== CREATE REQUEST HANDLERS ====================
  const handleCreateFormChange = (field, value) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    setCreateForm(prev => ({ ...prev, [field]: sanitizedValue }));
  };

  const handleCreateItemChange = (index, field, value) => {
    const updatedItems = [...createForm.items];
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    updatedItems[index][field] = sanitizedValue;

    if (field === 'quantity' || field === 'unit_price') {
      const quantity = sanitizeNumber(updatedItems[index].quantity);
      const unitPrice = sanitizeNumber(updatedItems[index].unit_price);
      updatedItems[index].total_price = quantity * unitPrice;
    }

    setCreateForm(prev => ({ ...prev, items: updatedItems }));
  };

  const handleCreateAddItem = () => {
    setCreateForm(prev => ({
      ...prev,
      items: [...prev.items, { 
        item_name: '', 
        quantity: '', 
        unit_price: '', 
        unit_of_measurement: '', 
        specification: '',
        item_id: null,
        total_price: 0
      }],
    }));
    console.debug('[PurchaseRequests] Added new item row to create form');
  };

  const handleCreateRemoveItem = (index) => {
    if (createForm.items.length === 1) {
      setSnackbar({ open: true, message: 'At least one item is required', severity: 'warning' });
      return;
    }
    const updatedItems = createForm.items.filter((_, i) => i !== index);
    setCreateForm(prev => ({ ...prev, items: updatedItems }));
    console.debug(`[PurchaseRequests] Removed item at index ${index} from create form`);
  };

  const handleCreateSelectInventoryItem = (index, item) => {
    console.debug(`[PurchaseRequests] Selecting inventory item for create:`, item);
    
    const updatedItems = [...createForm.items];
    
    updatedItems[index].item_name = item.item_name;
    updatedItems[index].unit_of_measurement = item.unit_of_measurement || '';
    updatedItems[index].item_id = item.id;
    
    if (item.category) {
      updatedItems[index].category = item.category;
    }
    
    setCreateForm(prev => ({ ...prev, items: updatedItems }));
    setCreateItemSearchInput('');
    setCreateItemSearchResults([]);
    
    console.debug(`[PurchaseRequests] Auto-filled unit_of_measurement: ${item.unit_of_measurement || 'not set'}`);
  };

  // ==================== EDIT REQUEST HANDLERS ====================
  const handleEditFormChange = (field, value) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    setEditForm(prev => ({ ...prev, [field]: sanitizedValue }));
  };

  const handleEditItemChange = (index, field, value) => {
    const updatedItems = [...editItems];
    const sanitizedValue = typeof value === 'string' ? sanitizeString(value) : value;
    updatedItems[index][field] = sanitizedValue;
    
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = sanitizeNumber(updatedItems[index].quantity);
      const unitPrice = sanitizeNumber(updatedItems[index].unit_price);
      updatedItems[index].total_price = quantity * unitPrice;
    }
    
    setEditItems(updatedItems);
    console.debug(`[PurchaseRequests] Edit item ${index} ${field} changed to:`, sanitizedValue);
  };

  const handleEditAddItem = () => {
    setEditItems([...editItems, { 
      id: null,
      item_name: '', 
      quantity: '', 
      unit_price: '', 
      unit_of_measurement: '', 
      specification: '',
      total_price: 0
    }]);
    console.debug('[PurchaseRequests] Added new item row to edit form');
  };

  const handleEditRemoveItem = (index) => {
    if (editItems.length === 1) {
      setSnackbar({ open: true, message: 'At least one item is required', severity: 'warning' });
      return;
    }
    const updatedItems = editItems.filter((_, i) => i !== index);
    setEditItems(updatedItems);
    console.debug(`[PurchaseRequests] Removed item at index ${index} from edit form`);
  };

  const handleEditSelectInventoryItem = (index, item) => {
    console.debug(`[PurchaseRequests] Selecting inventory item for edit:`, item);
    
    const updatedItems = [...editItems];
    
    updatedItems[index].item_name = item.item_name;
    updatedItems[index].unit_of_measurement = item.unit_of_measurement || '';
    updatedItems[index].item_id = item.id;
    
    if (item.category) {
      updatedItems[index].category = item.category;
    }
    
    setEditItems(updatedItems);
    setEditItemSearchInput('');
    setEditItemSearchResults([]);
    setCurrentEditItemIndex(null);
    
    console.debug(`[PurchaseRequests] Edit auto-filled unit_of_measurement: ${item.unit_of_measurement || 'not set'}`);
  };

  // ==================== CREATE PURCHASE REQUEST ====================
  const handleCreateRequest = async () => {
    logSecurityEvent('PURCHASE_REQUEST_CREATE_ATTEMPT', {
      itemCount: createForm.items.length,
      department: createForm.department,
    });

    if (!createForm.requested_by_phone) {
      setSnackbar({ open: true, message: 'Requester information is required', severity: 'error' });
      return;
    }

    const validItems = createForm.items.filter(item => 
      item.item_name && 
      item.item_name.trim() !== '' && 
      item.quantity && 
      item.unit_price
    );
    
    if (validItems.length === 0) {
      setSnackbar({ 
        open: true, 
        message: 'At least one complete item is required (name, quantity, unit price)', 
        severity: 'error' 
      });
      return;
    }

    for (const item of validItems) {
      const quantity = sanitizeNumber(item.quantity);
      const unitPrice = sanitizeNumber(item.unit_price);
      
      if (quantity <= 0) {
        setSnackbar({ 
          open: true, 
          message: `Quantity must be greater than 0 for item: ${item.item_name}`, 
          severity: 'error' 
        });
        return;
      }
      if (unitPrice <= 0) {
        setSnackbar({ 
          open: true, 
          message: `Unit price must be greater than 0 for item: ${item.item_name}`, 
          severity: 'error' 
        });
        return;
      }
    }

    setLoading(true);
    const startTime = performance.now();

    try {
      const baseUrl = getApiBaseUrl();
      const requestData = {
        ...createForm,
        department: HARDCODED_DEPARTMENT,
        items: validItems.map(item => ({
          item_name: sanitizeString(item.item_name),
          quantity: sanitizeNumber(item.quantity),
          unit_price: sanitizeNumber(item.unit_price),
          unit_of_measurement: sanitizeString(item.unit_of_measurement) || 'pcs',
          specification: sanitizeString(item.specification) || '',
          item_id: item.item_id || null,
        })),
        requested_by_name: userProfile.name || createForm.requested_by_name,
      };

      console.debug('[PurchaseRequests] Submitting request data:', requestData);
      
      const response = await axios.post(`${baseUrl}/hotel/inventory/purchase_requests.php`, requestData);

      if (response.data.success) {
        console.debug(`[PurchaseRequests] Request created in ${(performance.now() - startTime).toFixed(2)}ms`);
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setCreateDialogOpen(false);
        
        setCreateForm({
          requested_by_phone: userProfile.phone || '',
          requested_by_name: userProfile.name || '',
          department: HARDCODED_DEPARTMENT,
          delivery_date: '',
          notes: '',
          items: [{ 
            item_name: '', 
            quantity: '', 
            unit_price: '', 
            unit_of_measurement: '', 
            specification: '',
            item_id: null,
            total_price: 0
          }],
        });
        
        fetchRequests();
        logSecurityEvent('PURCHASE_REQUEST_CREATE_SUCCESS', { request_id: response.data.request_id });
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[PurchaseRequests] Error creating request:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to create request', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== UPDATE REQUEST ====================
  const handleUpdateRequest = async () => {
    if (!editForm) return;

    logSecurityEvent('PURCHASE_REQUEST_UPDATE_ATTEMPT', { request_id: editForm.id });

    const validItems = editItems.filter(item => 
      item.item_name && 
      item.item_name.trim() !== '' && 
      item.quantity && 
      item.unit_price
    );
    
    if (validItems.length === 0) {
      setSnackbar({ open: true, message: 'At least one complete item is required', severity: 'error' });
      return;
    }

    for (const item of validItems) {
      const quantity = sanitizeNumber(item.quantity);
      const unitPrice = sanitizeNumber(item.unit_price);
      if (quantity <= 0) {
        setSnackbar({ open: true, message: `Quantity must be greater than 0 for item: ${item.item_name}`, severity: 'error' });
        return;
      }
      if (unitPrice <= 0) {
        setSnackbar({ open: true, message: `Unit price must be greater than 0 for item: ${item.item_name}`, severity: 'error' });
        return;
      }
    }

    setLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.put(`${baseUrl}/hotel/inventory/purchase_requests.php`, {
        id: editForm.id,
        action: 'update',
        items: validItems.map(item => ({
          id: item.id,
          item_name: sanitizeString(item.item_name),
          quantity: sanitizeNumber(item.quantity),
          unit_price: sanitizeNumber(item.unit_price),
          unit_of_measurement: sanitizeString(item.unit_of_measurement) || 'pcs',
          specification: sanitizeString(item.specification) || '',
        })),
        notes: sanitizeString(editForm.notes || ''),
        delivery_date: editForm.delivery_date,
        staff_name: userProfile.name || 'System',
      });

      if (response.data.success) {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setEditDialogOpen(false);
        setEditForm(null);
        setEditItems([]);
        fetchRequests();
        
        if (viewDialogOpen && selectedRequest) {
          const refreshResponse = await axios.get(`${baseUrl}/hotel/inventory/purchase_requests.php?id=${selectedRequest.id}`);
          if (refreshResponse.data.success) {
            setSelectedRequest(refreshResponse.data.data);
          }
        }
        logSecurityEvent('PURCHASE_REQUEST_UPDATE_SUCCESS', { request_id: editForm.id });
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[PurchaseRequests] Error updating request:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to update request', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== APPROVAL HANDLERS ====================
  const handleApprove = async () => {
    if (!selectedRequest) return;

    logSecurityEvent(`PURCHASE_REQUEST_${approvalLevel.toUpperCase()}_APPROVE_ATTEMPT`, {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
    });

    setLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.put(`${baseUrl}/hotel/inventory/purchase_requests.php`, {
        id: selectedRequest.id,
        action: `${approvalLevel}_approve`,
        remark: sanitizeString(remarkText),
        staff_name: userProfile.name || 'System',
      });

      if (response.data.success) {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setApproveDialogOpen(false);
        setRemarkText('');
        setViewDialogOpen(false);
        fetchRequests();
        logSecurityEvent(`PURCHASE_REQUEST_${approvalLevel.toUpperCase()}_APPROVE_SUCCESS`, { request_id: selectedRequest.id });
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[PurchaseRequests] Error approving request:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to approve request', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    if (!remarkText || remarkText.trim().length < 3) {
      setSnackbar({ open: true, message: 'Please provide a detailed reason for rejection', severity: 'error' });
      return;
    }

    logSecurityEvent(`PURCHASE_REQUEST_${approvalLevel.toUpperCase()}_REJECT_ATTEMPT`, {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
    });

    setLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.put(`${baseUrl}/hotel/inventory/purchase_requests.php`, {
        id: selectedRequest.id,
        action: `${approvalLevel}_reject`,
        remark: sanitizeString(remarkText),
        staff_name: userProfile.name || 'System',
      });

      if (response.data.success) {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setApproveDialogOpen(false);
        setRemarkText('');
        setViewDialogOpen(false);
        fetchRequests();
        logSecurityEvent(`PURCHASE_REQUEST_${approvalLevel.toUpperCase()}_REJECT_SUCCESS`, { request_id: selectedRequest.id });
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[PurchaseRequests] Error rejecting request:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to reject request', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== AUDITOR REMARK ====================
  const handleAddAuditorRemark = async () => {
    if (!selectedRequest || !remarkText.trim()) {
      setSnackbar({ open: true, message: 'Please provide a remark', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.put(`${baseUrl}/hotel/inventory/purchase_requests.php`, {
        id: selectedRequest.id,
        action: 'add_auditor_remark',
        remark: sanitizeString(remarkText),
        staff_name: userProfile.name || 'Auditor',
      });

      if (response.data.success) {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setRemarkDialogOpen(false);
        setRemarkText('');
        fetchRequests();
        
        if (viewDialogOpen && selectedRequest) {
          const refreshResponse = await axios.get(`${baseUrl}/hotel/inventory/purchase_requests.php?id=${selectedRequest.id}`);
          if (refreshResponse.data.success) {
            setSelectedRequest(refreshResponse.data.data);
          }
        }
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[PurchaseRequests] Error adding remark:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to add remark', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== DELETE REQUEST ====================
  const handleDeleteRequest = async () => {
    if (!selectedRequest) return;

    if (!window.confirm(`⚠️ WARNING: You are about to delete Purchase Request #${selectedRequest.po_number}.\n\nThis action is PERMANENT and cannot be undone.\nAll associated items will also be deleted.\n\nClick OK to confirm deletion.`)) {
      return;
    }

    logSecurityEvent('PURCHASE_REQUEST_DELETE_ATTEMPT', {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
    });

    setLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.delete(`${baseUrl}/hotel/inventory/purchase_requests.php?id=${selectedRequest.id}`);

      if (response.data.success) {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setViewDialogOpen(false);
        setSelectedRequest(null);
        fetchRequests();
        logSecurityEvent('PURCHASE_REQUEST_DELETE_SUCCESS', { request_id: selectedRequest.id });
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[PurchaseRequests] Error deleting request:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to delete request', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== OPEN EDIT DIALOG ====================
  const openEditDialog = () => {
    if (selectedRequest) {
      console.debug('[PurchaseRequests] Opening edit dialog for request:', selectedRequest.po_number);
      setEditForm(JSON.parse(JSON.stringify(selectedRequest)));
      setEditItems(JSON.parse(JSON.stringify(selectedRequest.items || [])));
      setEditDialogOpen(true);
    }
  };

  // ==================== SEARCH ITEMS ====================
  const searchInventoryItemsForCreate = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCreateItemSearchResults([]);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = inventoryItems.filter(item =>
      item.item_name.toLowerCase().includes(searchLower) ||
      (item.category && item.category.toLowerCase().includes(searchLower))
    );

    console.debug(`[PurchaseRequests] Create search results for "${searchTerm}": ${filtered.length} items found`);
    setCreateItemSearchResults(filtered.slice(0, 15));
  }, [inventoryItems]);

  const searchInventoryItemsForEdit = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setEditItemSearchResults([]);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = inventoryItems.filter(item =>
      item.item_name.toLowerCase().includes(searchLower) ||
      (item.category && item.category.toLowerCase().includes(searchLower))
    );

    console.debug(`[PurchaseRequests] Edit search results for "${searchTerm}": ${filtered.length} items found`);
    setEditItemSearchResults(filtered.slice(0, 15));
  }, [inventoryItems]);

  // Debounce for create dialog search
  useEffect(() => {
    if (createItemSearchDebounce.current) clearTimeout(createItemSearchDebounce.current);
    createItemSearchDebounce.current = setTimeout(() => {
      searchInventoryItemsForCreate(createItemSearchInput);
    }, 300);
    return () => {
      if (createItemSearchDebounce.current) clearTimeout(createItemSearchDebounce.current);
    };
  }, [createItemSearchInput, searchInventoryItemsForCreate]);

  // Debounce for edit dialog search
  useEffect(() => {
    if (editItemSearchDebounce.current) clearTimeout(editItemSearchDebounce.current);
    editItemSearchDebounce.current = setTimeout(() => {
      searchInventoryItemsForEdit(editItemSearchInput);
    }, 300);
    return () => {
      if (editItemSearchDebounce.current) clearTimeout(editItemSearchDebounce.current);
    };
  }, [editItemSearchInput, searchInventoryItemsForEdit]);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    fetchRequests();
    fetchInventoryItems();
    fetchUserProfile();
  }, [fetchRequests, fetchInventoryItems, fetchUserProfile]);

  // ==================== DERIVED VALUES ====================
  const filteredRequests = useMemo(() => {
    let filtered = [...requests];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(req =>
        req.po_number?.toLowerCase().includes(term) ||
        req.requested_by_name?.toLowerCase().includes(term) ||
        req.department?.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [requests, searchTerm]);

  // ==================== ROLE PERMISSIONS ====================
  // IMPORTANT: These role values MUST match what your login system stores in localStorage
  // Based on your login component, the roles are:
  // - 'General_Manager' for GM
  // - 'Account' for Account Officer  
  // - 'Storekeeping' for Store Keeper
  // - 'admin' for Admin (has all permissions)
  
  const userRole = userProfile.user_type;
  
  const isAccount = userRole === 'Account' || userRole === 'admin';
  const isGM = userRole === 'General_Manager' || userRole === 'admin';
  const isStorekeeper = userRole === 'Storekeeping' || userRole === 'admin';
  const isAuditor = userRole === 'auditor' || userRole === 'admin';

  console.debug('[PurchaseRequests] ========== USER PERMISSIONS ==========');
  console.debug('[PurchaseRequests] Raw user_type from profile:', userRole);
  console.debug('[PurchaseRequests] isAccount:', isAccount, '(requires "Account" or "admin")');
  console.debug('[PurchaseRequests] isGM:', isGM, '(requires "General_Manager" or "admin")');
  console.debug('[PurchaseRequests] isStorekeeper:', isStorekeeper, '(requires "Storekeeping" or "admin")');
  console.debug('[PurchaseRequests] isAuditor:', isAuditor, '(requires "auditor" or "admin")');
  console.debug('[PurchaseRequests] User name:', userProfile.name);
  console.debug('[PurchaseRequests] ==========================================');

  // ==================== RENDER ====================
  const StatCard = ({ title, value, subtitle, color, icon: Icon }) => (
    <Card sx={{
      borderRadius: 3,
      height: '100%',
      boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s ease-in-out',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4] },
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>{title}</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color }}>{value}</Typography>
            {subtitle && <Typography variant="caption" color="textSecondary">{subtitle}</Typography>}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const getStatusChip = (status) => {
    const config = REQUEST_STATUSES[status] || { label: status, color: '#757575', icon: PendingIcon };
    return (
      <Chip
        label={config.label}
        size="small"
        icon={<config.icon sx={{ fontSize: 16 }} />}
        sx={{ bgcolor: alpha(config.color, 0.1), color: config.color, fontWeight: 'medium' }}
      />
    );
  };

  if (loading && requests.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading purchase requests...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        maxWidth: '1400px',
        mx: 'auto',
        width: '100%',
        overflow: 'auto',
        height: '100vh',
      }}>
        <TopNav
          onLogout={() => {
            logSecurityEvent('USER_LOGOUT', {});
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
            localStorage.removeItem('userName');
            localStorage.removeItem('userDepartment');
            localStorage.removeItem('role');
          }}
          onNotificationClick={() => console.debug('[PurchaseRequests] Notification clicked')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper elevation={0} sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #C84646 0%, #a33a3a 100%)',
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                Store Keeping - Purchase Orders
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Create purchase requests, track approvals, and generate secure purchase order documents
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip 
                  label={`Store Keeper: ${userProfile.name || 'Loading...'}`} 
                  size="small" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
                <Chip 
                  label={`Role: ${userProfile.user_type || 'Not set'}`} 
                  size="small" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
                <Chip 
                  label={`Department: ${HARDCODED_DEPARTMENT}`} 
                  size="small" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={fetchRequests}
                disabled={loading}
                sx={{ bgcolor: 'white', color: '#C84646', '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none' }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{ bgcolor: 'white', color: '#C84646', '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none' }}
              >
                New Purchase Request
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <StatCard title="Total Orders" value={stats?.total || 0} color="#C84646" icon={ReceiptIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <StatCard title="Pending Approval" value={stats?.pending || 0} color="#ff9800" icon={PendingIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <StatCard title="Account Approved" value={stats?.account_approved || 0} color="#2196f3" icon={CheckCircleIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <StatCard title="GM Approved" value={stats?.gm_approved || 0} color="#4caf50" icon={VerifiedIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <StatCard title="Completed" value={stats?.completed || 0} color="#4caf50" icon={CheckCircleIcon} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <StatCard title="Total Value" value={formatCurrency(stats?.total_value || 0)} color="#C84646" icon={MoneyIcon} />
          </Grid>
        </Grid>

        {/* Alert for Store Keeper Instructions */}
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>📌 Store Keeper Instructions:</strong> Once a purchase order is approved by GM, you can generate a secure Purchase Order document. 
          When items are received, mark them as received and they will be automatically added to <strong>inventory_supplies</strong> table for tracking.
        </Alert>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by PO number, requester, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 2, minWidth: 200 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="account_approved">Account Approved</MenuItem>
                <MenuItem value="gm_approved">GM Approved</MenuItem>
                <MenuItem value="partially_received">Partially Received</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Requests Table */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 2 }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 520px)', minHeight: '400px', overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>PO Number</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Requester</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Department</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>Total Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Items</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Date</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="textSecondary">No purchase requests found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((req) => (
                    <TableRow key={req.id} hover sx={{ height: '65px' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{req.po_number}</Typography>
                        {req.status === 'gm_approved' && (
                          <Chip size="small" label="Ready for PO" color="success" icon={<VerifiedIcon sx={{ fontSize: 12 }} />} sx={{ mt: 0.5, height: 20, fontSize: '10px' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{req.requested_by_name || 'N/A'}</Typography>
                          <Typography variant="caption" color="textSecondary">{req.requested_by_phone}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Chip size="small" label={req.department || 'N/A'} variant="outlined" /></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="#C84646">
                          {formatCurrency(req.total_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`${req.total_items} item(s) - ${req.pending_receipt_count || 0} pending receipt`}>
                          <Chip size="small" label={`${req.total_items} items`} icon={<InventoryIcon sx={{ fontSize: 14 }} />} variant="outlined" />
                        </Tooltip>
                      </TableCell>
                      <TableCell>{getStatusChip(req.status)}</TableCell>
                      <TableCell><Typography variant="caption">{formatDate(req.created_at)}</Typography></TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => { setSelectedRequest(req); setViewDialogOpen(true); }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(req.status === 'gm_approved' || req.status === 'account_approved') && (
                            <Tooltip title="Generate Purchase Order">
                              <IconButton 
                                size="small" 
                                sx={{ color: '#4caf50' }} 
                                onClick={() => { 
                                  setSelectedRequest(req); 
                                  setTimeout(() => handlePrintPO(), 100);
                                }}
                                disabled={printLoading}
                              >
                                {printLoading && selectedRequest?.id === req.id ? 
                                  <CircularProgress size={16} /> : 
                                  <PrintIcon fontSize="small" />
                                }
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredRequests.length > 0 && (
            <Box sx={{ p: 2, bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="body2">
                <strong>Showing:</strong> {filteredRequests.length} of {requests.length} requests
              </Typography>
            </Box>
          )}
        </Paper>

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary">
            Designed by Elegantè IT - Secure Purchase Order Management System
          </Typography>
        </footer>
      </Box>

      {/* ==================== CREATE DIALOG ==================== */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#C84646', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon /> Create Purchase Request
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Requester Name"
                  fullWidth
                  value={createForm.requested_by_name}
                  disabled
                  helperText="Auto-filled from your profile"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Department"
                  fullWidth
                  value={HARDCODED_DEPARTMENT}
                  disabled
                  helperText="Fixed department for Store Keeping"
                />
              </Grid>
            </Grid>

            <TextField
              type="date"
              label="Expected Delivery Date"
              fullWidth
              value={createForm.delivery_date}
              onChange={(e) => handleCreateFormChange('delivery_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Notes / Special Instructions"
              multiline
              rows={2}
              fullWidth
              value={createForm.notes}
              onChange={(e) => handleCreateFormChange('notes', e.target.value)}
              placeholder="Any additional notes for approval..."
            />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
              Items to Purchase
            </Typography>

            {createForm.items.map((item, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">Item #{index + 1}</Typography>
                  <IconButton size="small" color="error" onClick={() => handleCreateRemoveItem(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={inventoryItems}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.item_name || '';
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onInputChange={(event, newValue, reason) => {
                        if (reason === 'input') {
                          setCreateItemSearchInput(newValue);
                        }
                        handleCreateItemChange(index, 'item_name', newValue);
                      }}
                      onChange={(event, newValue) => {
                        if (newValue && typeof newValue !== 'string') {
                          handleCreateSelectInventoryItem(index, newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Item Name *"
                          placeholder="Search and select from inventory..."
                          required
                          helperText="Start typing to search inventory items"
                        />
                      )}
                      loading={inventoryItems.length === 0}
                      loadingText="Loading inventory..."
                      noOptionsText={createItemSearchInput.length < 2 ? "Type at least 2 characters" : "No matching items found"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Quantity *"
                      type="number"
                      fullWidth
                      value={item.quantity}
                      onChange={(e) => handleCreateItemChange(index, 'quantity', e.target.value)}
                      InputProps={{ inputProps: { min: 0.001, step: 0.001 } }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Unit Price (₦) *"
                      type="number"
                      fullWidth
                      value={item.unit_price}
                      onChange={(e) => handleCreateItemChange(index, 'unit_price', e.target.value)}
                      InputProps={{ inputProps: { min: 0.01, step: 0.01 } }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Unit of Measurement"
                      fullWidth
                      value={item.unit_of_measurement}
                      onChange={(e) => handleCreateItemChange(index, 'unit_of_measurement', e.target.value)}
                      placeholder="Auto-filled from inventory"
                      disabled={!!item.item_id}
                      helperText={item.item_id ? "Auto-filled from inventory selection" : "Enter manually if not in inventory"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Specification / Brand"
                      fullWidth
                      value={item.specification}
                      onChange={(e) => handleCreateItemChange(index, 'specification', e.target.value)}
                      placeholder="Brand, model, size, color, etc."
                    />
                  </Grid>
                  {item.quantity && item.unit_price && (
                    <Grid item xs={12}>
                      <Typography variant="body2" align="right" sx={{ color: '#C84646' }}>
                        Subtotal: {formatCurrency(sanitizeNumber(item.quantity) * sanitizeNumber(item.unit_price))}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}

            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreateAddItem} sx={{ alignSelf: 'flex-start' }}>
              Add Another Item
            </Button>

            <Alert severity="info">
              <strong>Total Request Amount:</strong> {formatCurrency(
                createForm.items.reduce((sum, item) => {
                  const qty = sanitizeNumber(item.quantity);
                  const price = sanitizeNumber(item.unit_price);
                  return sum + (qty * price);
                }, 0)
              )}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateRequest}
            variant="contained"
            sx={{ bgcolor: '#C84646', '&:hover': { bgcolor: '#a33a3a' } }}
            disabled={loading || createForm.items.filter(i => i.item_name && i.item_name.trim() !== '' && i.quantity && i.unit_price).length === 0}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== VIEW DETAILS DIALOG ==================== */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h6">Purchase Request Details</Typography>
              <Typography variant="body2" color="textSecondary">{selectedRequest?.po_number}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Close">
                <IconButton onClick={() => setViewDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
              {(selectedRequest?.status === 'gm_approved' || selectedRequest?.status === 'account_approved') && (
                <Tooltip title="Generate Purchase Order Document">
                  <IconButton onClick={handlePrintPO} sx={{ color: '#4caf50' }} disabled={printLoading}>
                    {printLoading ? <CircularProgress size={20} /> : <PrintIcon />}
                  </IconButton>
                </Tooltip>
              )}
              {(selectedRequest?.status === 'pending') && (
                <>
                  <Tooltip title="Edit Request">
                    <IconButton onClick={openEditDialog}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Request">
                    <IconButton onClick={handleDeleteRequest} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Stack spacing={3}>
              {/* Status Timeline */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Approval Status</Typography>
                <Stepper orientation="vertical" activeStep={
                  selectedRequest.status === 'pending' ? 0 :
                  selectedRequest.status === 'account_approved' ? 1 :
                  selectedRequest.status === 'gm_approved' ? 2 :
                  selectedRequest.status === 'partially_received' ? 3 :
                  selectedRequest.status === 'completed' ? 4 :
                  selectedRequest.status === 'rejected' ? -1 : 0
                }>
                  <Step>
                    <StepLabel StepIconComponent={({ active, completed }) =>
                      <PendingIcon color={active ? 'warning' : completed ? 'success' : 'disabled'} />
                    }>
                      <Typography variant="body2">Submitted by {selectedRequest.requested_by_name}</Typography>
                      <Typography variant="caption" color="textSecondary">{formatDateTime(selectedRequest.created_at)}</Typography>
                    </StepLabel>
                  </Step>
                  <Step>
                    <StepLabel StepIconComponent={({ active, completed }) =>
                      selectedRequest.account_approved_at ? <CheckCircleIcon color="success" /> :
                      <PendingIcon color={selectedRequest.status === 'pending' ? 'disabled' : 'warning'} />
                    }>
                      <Typography variant="body2">Account Approval</Typography>
                      {selectedRequest.account_approved_at && (
                        <Typography variant="caption" color="textSecondary">
                          Approved by {selectedRequest.account_approved_by} on {formatDateTime(selectedRequest.account_approved_at)}
                        </Typography>
                      )}
                    </StepLabel>
                    <StepContent>
                      {selectedRequest.account_remark?.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: '#f5f5f5' }}>
                          {selectedRequest.account_remark.map((remark, idx) => (
                            <Typography key={idx} variant="caption" display="block">
                              <strong>{remark.added_by}:</strong> {remark.remark} <em>({formatDateTime(remark.added_at)})</em>
                            </Typography>
                          ))}
                        </Paper>
                      )}
                    </StepContent>
                  </Step>
                  <Step>
                    <StepLabel StepIconComponent={({ active, completed }) =>
                      selectedRequest.gm_approved_at ? <VerifiedIcon color="success" /> :
                      <PendingIcon color={!selectedRequest.account_approved_at ? 'disabled' : 'warning'} />
                    }>
                      <Typography variant="body2">GM Approval</Typography>
                      {selectedRequest.gm_approved_at && (
                        <Typography variant="caption" color="textSecondary">
                          Approved by {selectedRequest.gm_approved_by} on {formatDateTime(selectedRequest.gm_approved_at)}
                        </Typography>
                      )}
                    </StepLabel>
                    <StepContent>
                      {selectedRequest.gm_remark?.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: '#f5f5f5' }}>
                          {selectedRequest.gm_remark.map((remark, idx) => (
                            <Typography key={idx} variant="caption" display="block">
                              <strong>{remark.added_by}:</strong> {remark.remark} <em>({formatDateTime(remark.added_at)})</em>
                            </Typography>
                          ))}
                        </Paper>
                      )}
                    </StepContent>
                  </Step>
                  <Step>
                    <StepLabel StepIconComponent={({ active, completed }) =>
                      selectedRequest.status === 'completed' ? <CheckCircleIcon color="success" /> :
                      <LocalShippingIcon color={!selectedRequest.gm_approved_at ? 'disabled' : 'warning'} />
                    }>
                      <Typography variant="body2">Store Receipt</Typography>
                      {selectedRequest.status === 'completed' && (
                        <Typography variant="caption" color="success.main">
                          All items received
                        </Typography>
                      )}
                      {selectedRequest.status === 'partially_received' && (
                        <Typography variant="caption" color="warning.main">
                          Some items pending receipt
                        </Typography>
                      )}
                    </StepLabel>
                  </Step>
                </Stepper>
              </Paper>

              {/* Items Table - UPDATED to show Mark Received button correctly */}
              <Typography variant="subtitle1" fontWeight="bold">Items Ordered</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>S/N</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Specification</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="center">Receipt Status</TableCell>
                      {/* Show Action column for Storekeeper on non-completed requests */}
                      {isStorekeeper && selectedRequest.status !== 'completed' && <TableCell align="center">Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedRequest.items || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isStorekeeper && selectedRequest.status !== 'completed' ? 9 : 8} align="center">
                          <Typography variant="body2" color="textSecondary">No items found for this purchase request.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (selectedRequest.items || []).map((item, idx) => {
                        const isFullyReceived = item.is_received && (item.received_quantity >= item.quantity);
                        const isPartiallyReceived = item.is_received && item.received_quantity > 0 && item.received_quantity < item.quantity;
                        const remainingQty = item.quantity - (item.received_quantity || 0);
                        const isPending = !isFullyReceived;
                        
                        return (
                          <TableRow key={item.id || idx}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{sanitizeString(item.item_name)}</TableCell>
                            <TableCell>{sanitizeString(item.specification || '-')}</TableCell>
                            <TableCell align="right">{sanitizeNumber(item.quantity).toLocaleString()}</TableCell>
                            <TableCell>{sanitizeString(item.unit_of_measurement || 'pcs')}</TableCell>
                            <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.total_price)}</TableCell>
                            <TableCell align="center">
                              {isFullyReceived ? (
                                <Chip size="small" label={`Received: ${sanitizeNumber(item.received_quantity).toLocaleString()}`} color="success" icon={<CheckIcon />} />
                              ) : isPartiallyReceived ? (
                                <Chip size="small" label={`Partially: ${sanitizeNumber(item.received_quantity).toLocaleString()}/${sanitizeNumber(item.quantity).toLocaleString()}`} color="warning" icon={<PendingIcon />} />
                              ) : (
                                <Chip size="small" label="Pending" color="warning" icon={<PendingIcon />} />
                              )}
                            </TableCell>
                            {/* Mark Received button - Show for Storekeeper on non-completed requests for pending items */}
                            {isStorekeeper && selectedRequest.status !== 'completed' && isPending && (
                              <TableCell align="center">
                                <Tooltip title={isPartiallyReceived ? `Receive remaining ${remainingQty} ${item.unit_of_measurement || 'units'}` : "Mark this item as received"}>
                                  <Button 
                                    size="small" 
                                    variant="outlined" 
                                    startIcon={<LocalShippingIcon />}
                                    onClick={() => {
                                      setSelectedItem(item);
                                      setReceiptForm({ 
                                        is_received: true, 
                                        received_quantity: remainingQty > 0 ? remainingQty : item.quantity, 
                                        remark: '' 
                                      });
                                      setReceiptDialogOpen(true);
                                    }}
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    {isPartiallyReceived ? `Receive Remaining (${remainingQty})` : 'Mark Received'}
                                  </Button>
                                </Tooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                    {(selectedRequest.items || []).length > 0 && (
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell colSpan={6} align="right"><strong>GRAND TOTAL:</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(selectedRequest.total_amount)}</strong></TableCell>
                        <TableCell />
                        {isStorekeeper && selectedRequest.status !== 'completed' && <TableCell />}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Bulk Receipt Button - UPDATED to show for Storekeeper on non-completed requests with pending items */}
              {isStorekeeper && selectedRequest.status !== 'completed' && 
                (selectedRequest.items || []).filter(item => {
                  const isFullyReceived = item.is_received && (item.received_quantity >= item.quantity);
                  return !isFullyReceived && item.quantity > 0;
                }).length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <LocalShippingIcon />}
                    onClick={handleBulkItemReceipt}
                    disabled={loading}
                    sx={{ 
                      bgcolor: '#ff9800', 
                      '&:hover': { bgcolor: '#ed6c02' },
                      textTransform: 'none'
                    }}
                  >
                    {loading ? 'Processing...' : `Bulk Receive Pending Items (${(selectedRequest.items || []).filter(item => {
                      const isFullyReceived = item.is_received && (item.received_quantity >= item.quantity);
                      return !isFullyReceived && item.quantity > 0;
                    }).length} items)`}
                  </Button>
                </Box>
              )}

              {/* Notes */}
              {selectedRequest.notes && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Notes:</Typography>
                  <Typography variant="body2">{selectedRequest.notes}</Typography>
                </Paper>
              )}

              {/* Delivery Date */}
              {selectedRequest.delivery_date && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Expected Delivery Date:</Typography>
                  <Typography variant="body2">{formatDate(selectedRequest.delivery_date)}</Typography>
                </Paper>
              )}

              {/* Auditor Remarks Section */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Auditor Remarks</Typography>
                  {isAuditor && (
                    <Button size="small" startIcon={<CommentIcon />} onClick={() => {
                      setSelectedRequest(selectedRequest);
                      setRemarkText('');
                      setRemarkDialogOpen(true);
                    }}>
                      Add Remark
                    </Button>
                  )}
                </Box>
                {selectedRequest.auditor_remark?.length > 0 ? (
                  selectedRequest.auditor_remark.map((remark, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1, mt: 1, bgcolor: '#f5f5f5' }}>
                      <Typography variant="caption" display="block">
                        <strong>{remark.added_by}:</strong> {remark.remark}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {formatDateTime(remark.added_at)}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">No auditor remarks yet.</Typography>
                )}
              </Paper>

              {/* Completion Alert */}
              {areAllItemsReceived(selectedRequest) && selectedRequest.status !== 'completed' && (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  ✅ All items have been received! This purchase order will be marked as completed.
                </Alert>
              )}

              {/* Approval Buttons - UPDATED with correct role checks */}
              {(selectedRequest.status === 'pending' && isAccount) && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => {
                    setSelectedRequest(selectedRequest);
                    setApprovalLevel('account');
                    setSelectedAction('reject');
                    setRemarkText('');
                    setApproveDialogOpen(true);
                  }}>
                    Reject
                  </Button>
                  <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={() => {
                    setSelectedRequest(selectedRequest);
                    setApprovalLevel('account');
                    setSelectedAction('approve');
                    setRemarkText('');
                    handleApprove();
                  }} sx={{ bgcolor: '#4caf50' }}>
                    Approve as Account
                  </Button>
                </Box>
              )}

              {(selectedRequest.status === 'account_approved' && isGM) && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => {
                    setSelectedRequest(selectedRequest);
                    setApprovalLevel('gm');
                    setSelectedAction('reject');
                    setRemarkText('');
                    setApproveDialogOpen(true);
                  }}>
                    Reject
                  </Button>
                  <Button variant="contained" startIcon={<VerifiedIcon />} onClick={() => {
                    setSelectedRequest(selectedRequest);
                    setApprovalLevel('gm');
                    setSelectedAction('approve');
                    setRemarkText('');
                    handleApprove();
                  }} sx={{ bgcolor: '#4caf50' }}>
                    Approve as GM
                  </Button>
                </Box>
              )}

              {/* Purchase Order Generation Button for Store Keeper */}
              {(isStorekeeper && (selectedRequest.status === 'gm_approved' || selectedRequest.status === 'account_approved')) && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, pt: 2, borderTop: 2, borderColor: '#4caf50' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={printLoading ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                    onClick={handlePrintPO}
                    disabled={printLoading}
                    sx={{ 
                      bgcolor: '#4caf50', 
                      '&:hover': { bgcolor: '#2e7d32' },
                      px: 4,
                      py: 1.5,
                      fontSize: '16px'
                    }}
                  >
                    {printLoading ? 'Generating Secure Document...' : 'Generate Secure Purchase Order'}
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ==================== EDIT DIALOG ==================== */}
      <Dialog open={editDialogOpen} onClose={() => {
        setEditDialogOpen(false);
        setEditForm(null);
        setEditItems([]);
      }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#C84646', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon /> Edit Purchase Request
          </Box>
        </DialogTitle>
        <DialogContent>
          {editForm && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Alert severity="info">
                <strong>PO Number:</strong> {editForm.po_number}
              </Alert>

              <TextField
                type="date"
                label="Expected Delivery Date"
                fullWidth
                value={editForm.delivery_date || ''}
                onChange={(e) => handleEditFormChange('delivery_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Notes / Special Instructions"
                multiline
                rows={3}
                fullWidth
                value={editForm.notes || ''}
                onChange={(e) => handleEditFormChange('notes', e.target.value)}
                placeholder="Any additional notes for approval..."
              />

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
                Items to Purchase
              </Typography>

              {editItems.map((item, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">Item #{idx + 1}</Typography>
                    <IconButton size="small" color="error" onClick={() => handleEditRemoveItem(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        freeSolo
                        options={inventoryItems}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.item_name || '';
                        }}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={item.item_name ? { item_name: item.item_name, unit_of_measurement: item.unit_of_measurement } : null}
                        onInputChange={(event, newValue, reason) => {
                          if (reason === 'input') {
                            setEditItemSearchInput(newValue);
                            setCurrentEditItemIndex(idx);
                          }
                          handleEditItemChange(idx, 'item_name', newValue);
                        }}
                        onChange={(event, newValue) => {
                          if (newValue && typeof newValue !== 'string') {
                            handleEditSelectInventoryItem(idx, newValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Item Name *"
                            placeholder="Search and select from inventory..."
                            required
                            helperText="Start typing to search inventory items"
                          />
                        )}
                        loading={inventoryItems.length === 0}
                        loadingText="Loading inventory..."
                        noOptionsText={editItemSearchInput.length < 2 ? "Type at least 2 characters" : "No matching items found"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Quantity *"
                        type="number"
                        fullWidth
                        value={item.quantity}
                        onChange={(e) => handleEditItemChange(idx, 'quantity', e.target.value)}
                        InputProps={{ inputProps: { min: 0.001, step: 0.001 } }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Unit Price (₦) *"
                        type="number"
                        fullWidth
                        value={item.unit_price}
                        onChange={(e) => handleEditItemChange(idx, 'unit_price', e.target.value)}
                        InputProps={{ inputProps: { min: 0.01, step: 0.01 } }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Unit of Measurement"
                        fullWidth
                        value={item.unit_of_measurement || ''}
                        onChange={(e) => handleEditItemChange(idx, 'unit_of_measurement', e.target.value)}
                        placeholder="e.g., pcs, kg, litres"
                        disabled={!!item.item_id}
                        helperText={item.item_id ? "Auto-filled from inventory selection" : "Enter manually if not in inventory"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Specification / Brand"
                        fullWidth
                        value={item.specification || ''}
                        onChange={(e) => handleEditItemChange(idx, 'specification', e.target.value)}
                        placeholder="Brand, model, size, color, etc."
                      />
                    </Grid>
                    {item.quantity && item.unit_price && (
                      <Grid item xs={12}>
                        <Typography variant="body2" align="right" sx={{ color: '#C84646' }}>
                          Subtotal: {formatCurrency(sanitizeNumber(item.quantity) * sanitizeNumber(item.unit_price))}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))}

              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleEditAddItem} sx={{ alignSelf: 'flex-start' }}>
                Add Another Item
              </Button>

              <Alert severity="info">
                <strong>Total Request Amount:</strong> {formatCurrency(
                  editItems.reduce((sum, item) => {
                    const qty = sanitizeNumber(item.quantity);
                    const price = sanitizeNumber(item.unit_price);
                    return sum + (qty * price);
                  }, 0)
                )}
              </Alert>

              <Alert severity="warning">
                <strong>Note:</strong> Editing a purchase request that has already been submitted will require re-approval from Account and GM.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditForm(null);
            setEditItems([]);
          }}>Cancel</Button>
          <Button onClick={handleUpdateRequest} variant="contained" sx={{ bgcolor: '#C84646' }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== APPROVAL/REJECTION DIALOG ==================== */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedAction === 'reject' ? 'Reject Purchase Request' : 'Confirm Approval'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info">
              <strong>PO Number:</strong> {selectedRequest?.po_number}<br />
              <strong>Total Amount:</strong> {formatCurrency(selectedRequest?.total_amount)}<br />
              <strong>Requester:</strong> {selectedRequest?.requested_by_name}
            </Alert>
            <TextField
              label={selectedAction === 'reject' ? "Rejection Reason *" : "Remark (Optional)"}
              multiline
              rows={3}
              fullWidth
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
              placeholder={selectedAction === 'reject' ? "Please provide a detailed reason for rejection..." : "Add any remarks (optional)..."}
              required={selectedAction === 'reject'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={selectedAction === 'reject' ? handleReject : handleApprove}
            variant="contained"
            sx={{ bgcolor: selectedAction === 'reject' ? '#f44336' : '#4caf50' }}
            disabled={selectedAction === 'reject' && (!remarkText || remarkText.length < 3)}
          >
            {selectedAction === 'reject' ? 'Confirm Rejection' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== REMARK DIALOG ==================== */}
      <Dialog open={remarkDialogOpen} onClose={() => setRemarkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Auditor Remark</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info">
              <strong>PO Number:</strong> {selectedRequest?.po_number}
            </Alert>
            <TextField
              label="Remark *"
              multiline
              rows={3}
              fullWidth
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
              placeholder="Add your observation or comment..."
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddAuditorRemark} variant="contained" sx={{ bgcolor: '#C84646' }}>
            Submit Remark
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== ITEM RECEIPT DIALOG ==================== */}
      <Dialog open={receiptDialogOpen} onClose={() => setReceiptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4caf50', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon /> Mark Item as Received
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info">
              <strong>📦 Item:</strong> {selectedItem?.item_name}<br />
              <strong>📊 Ordered Quantity:</strong> {sanitizeNumber(selectedItem?.quantity).toLocaleString()} {selectedItem?.unit_of_measurement || 'units'}<br />
              <strong>📊 Already Received:</strong> {sanitizeNumber(selectedItem?.received_quantity || 0).toLocaleString()} {selectedItem?.unit_of_measurement || 'units'}<br />
              <strong>💰 Unit Price:</strong> {formatCurrency(selectedItem?.unit_price)}<br />
              <strong>📋 PO Number:</strong> {selectedRequest?.po_number}
            </Alert>
            
            <Alert severity="warning">
              <strong>⚠️ Important:</strong> Once you mark this item as received, it will be automatically added to the <strong>inventory_supplies</strong> table and will be available for store requisitions. The supply record will be pending approval by an auditor.
            </Alert>
            
            <TextField
              type="number"
              label="Quantity Received *"
              fullWidth
              value={receiptForm.received_quantity}
              onChange={(e) => setReceiptForm(prev => ({ ...prev, received_quantity: parseFloat(e.target.value) }))}
              InputProps={{ 
                inputProps: { min: 0.001, max: (selectedItem?.quantity - (selectedItem?.received_quantity || 0)), step: 0.001 },
                endAdornment: <InputAdornment position="end">{selectedItem?.unit_of_measurement || 'units'}</InputAdornment>
              }}
              helperText={`Maximum: ${sanitizeNumber(selectedItem?.quantity - (selectedItem?.received_quantity || 0)).toLocaleString()} ${selectedItem?.unit_of_measurement || 'units'}`}
            />
            
            <TextField
              label="Remark (Optional)"
              multiline
              rows={3}
              fullWidth
              value={receiptForm.remark}
              onChange={(e) => setReceiptForm(prev => ({ ...prev, remark: e.target.value }))}
              placeholder="Enter any observations, quality notes, or additional information about the received items..."
            />
            
            <Alert severity="info">
              <strong>📝 What happens next?</strong><br />
              1. Item receipt status will be updated in this purchase order<br />
              2. Item will be added to <strong>inventory_supplies</strong> table<br />
              3. Auditor will need to approve the supply record<br />
              4. Once approved, item becomes available in inventory for requisitions
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateItemReceipt} 
            variant="contained" 
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#2e7d32' } }}
            disabled={!receiptForm.received_quantity || receiptForm.received_quantity <= 0}
          >
            Confirm Receipt & Add to Inventory
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchaseRequests;